/**
 * AttachToFlowDialog — anexa um e-mail (campanha) a um fluxo de automação.
 *
 * UX: lista os fluxos de e-mail do tenant, permite buscar, e ao confirmar
 * adiciona um nó email_content (referenciando essa campanha) ao final do
 * fluxo, ligado pelo último nó executável existente. Ao salvar, oferece
 * "Ir para o fluxo".
 */

import { useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { toUserFriendlyError } from '@/lib/error-messages';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  GitBranch,
  Search,
  Mail,
  CheckCircle2,
  ChevronRight,
  Plus,
  Link2,
} from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Conta quantos nós email_content em um fluxo referenciam essa campanha.
 * O nó pode usar emailSource='campaign' (fluxo novo) ou config legada
 * (sem emailSource, mas com emailId apontando pra emailCampaigns).
 */
function countAttachmentsInFlow(flowNodes: any[] | undefined, campaignId: number): number {
  if (!Array.isArray(flowNodes)) return 0;
  let count = 0;
  for (const n of flowNodes) {
    if (n?.type !== 'email_content') continue;
    const data = n.data ?? {};
    const source = data.emailSource ?? 'campaign';
    if (data.emailId === campaignId && source === 'campaign') count++;
  }
  return count;
}

interface AttachToFlowDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  campaignId: number;
  campaignName: string;
  campaignSubject?: string;
}

export default function AttachToFlowDialog({
  open,
  onOpenChange,
  campaignId,
  campaignName,
  campaignSubject,
}: AttachToFlowDialogProps) {
  const [, navigate] = useLocation();
  const [search, setSearch] = useState('');
  const [selectedFlowId, setSelectedFlowId] = useState<number | null>(null);

  const utils = trpc.useUtils();
  const { data: flows = [], isLoading } = trpc.flows.list.useQuery(
    { channel: 'email' },
    { enabled: open },
  ) as { data?: Array<{ id: number; name: string; status: string; nodes?: any[]; edges?: any[] }>; isLoading: boolean };

  // Pré-calcula, pra cada fluxo, quantas vezes esta campanha aparece como nó.
  const enriched = useMemo(
    () =>
      flows.map((f) => ({
        ...f,
        attachedCount: countAttachmentsInFlow(f.nodes as any[] | undefined, campaignId),
      })),
    [flows, campaignId],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return enriched;
    return enriched.filter((f) => (f.name ?? '').toLowerCase().includes(q));
  }, [enriched, search]);

  const alreadyAttachedFlows = useMemo(
    () => enriched.filter((f) => f.attachedCount > 0),
    [enriched],
  );

  const updateFlow = trpc.flows.update.useMutation();

  const createFlow = trpc.flows.create.useMutation({
    onSuccess: async (created: any) => {
      utils.flows.list.invalidate();
      // Refetch e seleciona o recém-criado
      setSelectedFlowId(created.id);
      toast.success(`Fluxo "${created.name}" criado.`);
    },
    onError: (err) => toast.error(toUserFriendlyError(err, 'Falha ao criar fluxo')),
  });

  const handleAttach = async () => {
    if (!selectedFlowId) {
      toast.error('Selecione um fluxo destino.');
      return;
    }
    const flow = flows.find((f) => f.id === selectedFlowId);
    if (!flow) return;

    const existingNodes = (flow.nodes ?? []) as any[];
    const existingEdges = (flow.edges ?? []) as any[];

    // Calcula nova posição: à direita do último nó.
    const maxX = existingNodes.reduce(
      (m, n) => Math.max(m, n?.position?.x ?? 0),
      0,
    );
    // ID estável e único — segue o padrão "node-<uuid>" para não colidir com
    // o counter incremental "node-N" usado no FlowBuilder ao adicionar nós
    // novos manualmente.
    const newNodeId = `node-${
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${campaignId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    }`;
    const newNode = {
      id: newNodeId,
      type: 'email_content',
      position: { x: (maxX || 0) + 280, y: 100 },
      data: {
        label: `E-mail: ${campaignName}`.slice(0, 80),
        emailId: campaignId,
        emailSource: 'campaign',
        selectedEmailName: campaignName,
        emailSubject: campaignSubject ?? '',
      },
    };

    // Liga ao último nó existente (fallback: trigger), se houver.
    const tail =
      [...existingNodes].reverse().find((n) => n?.type !== 'end') ??
      existingNodes[0];
    const newEdges = tail
      ? [
          ...existingEdges,
          {
            id: `edge-${tail.id}-${newNodeId}`,
            source: tail.id,
            target: newNodeId,
          },
        ]
      : existingEdges;

    try {
      await updateFlow.mutateAsync({
        id: selectedFlowId,
        nodes: [...existingNodes, newNode] as any,
        edges: newEdges as any,
      });
      utils.flows.list.invalidate();
      utils.flows.get.invalidate({ id: selectedFlowId });
      toast.success(`E-mail anexado ao fluxo "${flow.name}".`, {
        action: {
          label: 'Ir para o fluxo',
          onClick: () => navigate(`/flows/${selectedFlowId}`),
        },
      });
      onOpenChange(false);
    } catch (err) {
      toast.error(toUserFriendlyError(err, 'Falha ao anexar'));
    }
  };

  const handleCreateAndAttach = () => {
    const name = `${campaignName} (fluxo)`.slice(0, 100);
    createFlow.mutate({
      name,
      channel: 'email',
      kind: 'automation',
      triggerType: 'manual',
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-xl mx-2 sm:mx-0">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitBranch className="w-5 h-5 text-indigo-600" />
            Anexar este e-mail a um fluxo
          </DialogTitle>
          <DialogDescription>
            O e-mail vira um nó "Enviar E-mail" no fluxo escolhido, ligado ao
            último passo existente. Você poderá reorganizar no canvas depois.
          </DialogDescription>
        </DialogHeader>

        <Card className="p-3 bg-blue-50/40 border-blue-200">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-blue-600" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{campaignName}</p>
              {campaignSubject && (
                <p className="text-xs text-muted-foreground truncate">{campaignSubject}</p>
              )}
            </div>
          </div>
        </Card>

        {/* Resumo de anexações já existentes — sinaliza que o e-mail
            é reutilizável, mas avisa onde já está em uso. */}
        {alreadyAttachedFlows.length > 0 && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-2.5 space-y-1.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-800">
              <Link2 className="w-3.5 h-3.5" />
              Já está em {alreadyAttachedFlows.length} fluxo{alreadyAttachedFlows.length === 1 ? '' : 's'}:
            </div>
            <div className="flex flex-wrap gap-1.5">
              {alreadyAttachedFlows.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => navigate(`/flows/${f.id}`)}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-emerald-200 text-[11px] hover:bg-emerald-100 transition-colors"
                  title="Abrir esse fluxo"
                >
                  <GitBranch className="w-3 h-3 text-emerald-700" />
                  <span className="text-emerald-900 truncate max-w-[180px]">{f.name}</span>
                  {f.attachedCount > 1 && (
                    <span className="text-emerald-700 font-semibold">×{f.attachedCount}</span>
                  )}
                </button>
              ))}
            </div>
            <p className="text-[10.5px] text-emerald-700/80 leading-snug">
              Pode anexar novamente — o e-mail será reusado em outra posição do fluxo.
            </p>
          </div>
        )}

        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="relative flex-1">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar fluxo por nome…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="text-sm pl-8"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 shrink-0"
              onClick={handleCreateAndAttach}
              disabled={createFlow.isPending}
            >
              <Plus className="w-3.5 h-3.5" />
              {createFlow.isPending ? 'Criando…' : 'Novo fluxo'}
            </Button>
          </div>

          <div className="max-h-[280px] overflow-y-auto border rounded-md divide-y">
            {isLoading ? (
              <p className="text-sm text-muted-foreground p-4 text-center">Carregando fluxos…</p>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground p-4 text-center">
                {search ? 'Nada encontrado.' : 'Nenhum fluxo de e-mail criado ainda.'}
              </p>
            ) : (
              filtered.map((f) => {
                const isSelected = selectedFlowId === f.id;
                const emailCount = ((f.nodes as any[]) ?? []).filter((n) => n?.type === 'email_content').length;
                const alreadyHere = f.attachedCount > 0;
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setSelectedFlowId(f.id)}
                    className={cn(
                      'w-full text-left px-3 py-2.5 flex items-center gap-2 hover:bg-muted/50 transition-colors',
                      isSelected && 'bg-indigo-50 hover:bg-indigo-50',
                    )}
                  >
                    <GitBranch className={cn('w-4 h-4 shrink-0', isSelected ? 'text-indigo-600' : 'text-muted-foreground')} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium truncate">{f.name}</span>
                        <Badge variant="outline" className="text-[10px] shrink-0">{f.status}</Badge>
                        {alreadyHere && (
                          <Badge
                            variant="default"
                            className="text-[10px] shrink-0 bg-emerald-600 hover:bg-emerald-600 gap-0.5 pl-1.5"
                          >
                            <CheckCircle2 className="w-3 h-3" />
                            já anexado{f.attachedCount > 1 ? ` ×${f.attachedCount}` : ''}
                          </Badge>
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        {emailCount} e-mail{emailCount === 1 ? '' : 's'} no fluxo
                        {alreadyHere && ' · selecionar para anexar de novo'}
                      </p>
                    </div>
                    {isSelected ? (
                      <CheckCircle2 className="w-4 h-4 text-indigo-600 shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-muted-foreground/40 shrink-0" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleAttach}
            disabled={!selectedFlowId || updateFlow.isPending}
            className="gap-1.5"
          >
            <GitBranch className="w-4 h-4" />
            {updateFlow.isPending
              ? 'Anexando…'
              : selectedFlowId && enriched.find((f) => f.id === selectedFlowId)?.attachedCount
                ? 'Anexar de novo'
                : 'Anexar ao fluxo'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
